import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import UserPatternSettingUpdate from "./_111_user_pattern_setting_update.js";
import { useDateRange } from "./dateRangeContext.js";
import { formatDateMMDDYY } from "./patterns/helpers.js";
import { logEvent } from "./eventLogger.js";
import { getCachedPatternData, setCachedPatternData } from "./patterns/cacheHelper.js"; // ✅ NEW
import "./styles.css";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from "chart.js";
import { Chart } from "react-chartjs-2";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    ChartTooltip,
    Legend,
    Filler
);

// --- Gear Icon Unicode ---
const GearIcon = ({ size = 24, color = "#fff" }) => (
    <span
        style={{
            fontSize: size,
            color,
            verticalAlign: "middle",
            cursor: "pointer",
            userSelect: "none",
            lineHeight: 1,
            display: "inline-block",
        }}
        title="Settings"
        aria-label="Settings"
    >
        &#9881;
    </span>
);

const MODULE_NAME = "95_pattern_near_open";

const Pattern_near_open = ({ userId: propUserId }) => {
    const { windowDays } = useDateRange();

    // Derived userId with fallback
    const storedId = sessionStorage.getItem("user_id");
    const userId = propUserId || (storedId && !isNaN(Number(storedId)) ? Number(storedId) : 1);

    const [date, setDate] = useState("");
    const [tooltip, setTooltip] = useState("");
    const [nearOpenPattern, setNearOpenPattern] = useState(null);
    const [totalPL, setTotalPL] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [chartMode, setChartMode] = useState("cumulative");
    const [showSettings, setShowSettings] = useState(false);
    const [patternId, setPatternId] = useState(null);
    const [patternConfig, setPatternConfig] = useState(null);
    const [fromCache, setFromCache] = useState(false); // ✅ Track cache status
    const chartContainerRef = useRef(null);

    function getLookbackDays(val) {
        if (!val) return 30;
        if (!isNaN(Number(val))) return Number(val);
        if (val === "YTD") return 365;
        return 30;
    }

    // ✅ UPDATED: Fetch data with async database cache
    const fetchPatternData = async (days) => {
        setLoading(true);
        
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/_95_pattern_near_open`,
                {
                    params: { lookback_days: days },
                    withCredentials: true,
                }
            );
            
            setDate(res.data.date);
            setTooltip(res.data.tooltip);
            setNearOpenPattern(res.data);
            setTotalPL(res.data.total_pl);

            // ✅ UPDATED: Extract numeric pattern_id from API response
            const numericPatternId = res.data.pattern_id;
            setPatternId(numericPatternId);
            setPatternConfig(res.data.config || {});

            // ✅ UPDATED: Use numeric pattern_id as cache key
            if (numericPatternId) {
                await setCachedPatternData(
                    `pattern_${numericPatternId}`,
                    days,
                    res.data
                );
            }

            console.log("Pattern ID:", numericPatternId, "Config:", res.data.config);
            setFromCache(false);
        } catch (err) {
            console.error("Error fetching pattern data:", err);
            
            // ✅ Try to load from cache on error
            if (patternId) {
                const cachedData = await getCachedPatternData(
                    `pattern_${patternId}`,
                    days
                );
                if (cachedData) {
                    setDate(cachedData.date);
                    setTooltip(cachedData.tooltip);
                    setNearOpenPattern(cachedData);
                    setTotalPL(cachedData.total_pl);
                    setPatternConfig(cachedData.config || {});
                    setFromCache(true);
                    setLoading(false);
                    return;
                }
            }
            
            setTooltip("Error fetching data.");
            setNearOpenPattern(null);
            setTotalPL(null);
            setPatternId(null);
        }
        
        setLoading(false);
    };

    useEffect(() => {
        const lookback = getLookbackDays(windowDays);
        if (lookback) {
            fetchPatternData(lookback);
        }
        // eslint-disable-next-line
    }, [windowDays]);

    const formatTime = (timeStr) => {
        if (!timeStr) return "";
        const [hour, minute] = timeStr.split(":");
        const h = parseInt(hour, 10);
        const ampm = h >= 12 ? "PM" : "AM";
        const displayHour = h % 12 === 0 ? 12 : h % 12;
        return `${displayHour}:${minute} ${ampm}`;
    };

    // --- Settings Modal ---
    const renderSettingsModal = () => {
        if (!showSettings || !patternId) return null;
        return (
            <>
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(24,26,37,0.5)",
                        backdropFilter: "blur(6px)",
                        zIndex: 9998,
                    }}
                    onClick={() => setShowSettings(false)}
                />
                <div
                    style={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 9999,
                        minWidth: 600,
                        minHeight: 420,
                        background: "#181A25",
                        borderRadius: 12,
                        boxShadow: "0 2px 8px rgba(0,255,255,0.08)",
                        padding: 0,
                    }}
                >
                    <UserPatternSettingUpdate
                        patternId={patternId}
                        countThreshold={patternConfig?.count_threshold}
                        thresholdDefinition={patternConfig?.threshold_definition}
                        thresholdValues={{
                            threshold_1: nearOpenPattern?.threshold_1_used,
                            threshold_2: nearOpenPattern?.threshold_2_used,
                            default_lookback_window: nearOpenPattern?.default_lookback_window_used
                        }}
                    />
                    <button
                        onClick={() => setShowSettings(false)}
                        style={{
                            position: "absolute",
                            top: 12,
                            right: 18,
                            background: "none",
                            border: "none",
                            color: "#fff",
                            fontSize: 22,
                            cursor: "pointer",
                        }}
                        title="Close"
                    >
                        ✕
                    </button>
                </div>
            </>
        );
    };

    const getChartData = () => {
        if (!nearOpenPattern || !Array.isArray(nearOpenPattern.time_evolution))
            return [];
        if (chartMode === "cumulative") {
            return nearOpenPattern.time_evolution;
        }
        if (chartMode === "rolling") {
            const arr = nearOpenPattern.time_evolution;
            const window = 5;
            return arr.map((d, i) => {
                const slice = arr.slice(Math.max(0, i - window + 1), i + 1);
                const avgPL =
                    slice.reduce((sum, x) => sum + (x.daily_pl || 0), 0) / slice.length;
                const avgWin =
                    (slice.reduce((sum, x) => sum + (x.daily_wins || 0), 0) /
                        (slice.reduce((sum, x) => sum + (x.daily_trades || 0), 0) || 1)) *
                    100;
                const avgFreq =
                    slice.reduce((sum, x) => sum + (x.frequency || 0), 0) / slice.length;
                return {
                    ...d,
                    rolling_pl: avgPL,
                    rolling_win_rate: avgWin,
                    rolling_frequency: avgFreq,
                };
            });
        }
        if (chartMode === "scatter" && Array.isArray(nearOpenPattern.pl_matches)) {
            return nearOpenPattern.pl_matches.map((d, i) => ({
                ...d,
                index: i + 1,
            }));
        }
        return [];
    };

    const getChartLabels = (data) =>
        data.map((d) => formatDateMMDDYY(d.date || d.trade_date));

    const buildChartData = () => {
        const data = getChartData();
        if (!data.length) return { labels: [], datasets: [] };

        if (chartMode === "scatter") {
            return {
                labels: getChartLabels(data),
                datasets: [
                    {
                        type: "scatter",
                        label: "Trade P/L",
                        data: data.map((d, idx) => ({
                            x: idx,
                            y: d.pl,
                        })),
                        backgroundColor: data.map((d) =>
                            d.pl >= 0 ? "rgba(76, 175, 80, 0.6)" : "rgba(244, 67, 54, 0.6)"
                        ),
                        pointRadius: 6,
                        yAxisID: "y",
                    },
                ],
            };
        }

        const plKey = chartMode === "rolling" ? "rolling_pl" : "cumulative_pl";
        const winKey = chartMode === "rolling" ? "rolling_win_rate" : "cumulative_win_rate";
        const freqKey = chartMode === "rolling" ? "rolling_frequency" : "frequency";
        const winsKey = chartMode === "rolling" ? "rolling_wins" : "daily_wins";

        // Calculate wins and losses for each data point
        const wins = data.map(d => d[winsKey] || 0);
        const freq = data.map(d => d[freqKey] || 0);
        const losses = freq.map((f, i) => Math.max(0, f - wins[i]));

        return {
            labels: getChartLabels(data),
            datasets: [
                {
                    type: "line",
                    label: "P/L",
                    data: data.map((d) => d[plKey]),
                    borderColor: "#1976d2",
                    backgroundColor: "rgba(25, 118, 210, 0.1)",
                    borderWidth: 3,
                    pointRadius: 3,
                    fill: true,
                    tension: 0.3,
                    yAxisID: "y",
                },
                {
                    type: "line",
                    label: "Win Rate %",
                    data: data.map((d) => d[winKey]),
                    borderColor: "#4caf50",
                    backgroundColor: "rgba(76, 175, 80, 0.1)",
                    borderWidth: 2,
                    pointRadius: 2,
                    fill: true,
                    tension: 0.3,
                    yAxisID: "y1",
                },
                {
                    type: "bar",
                    label: "Wins",
                    data: wins,
                    backgroundColor: "rgba(76, 175, 80, 0.6)",
                    borderColor: "#4caf50",
                    borderWidth: 1,
                    yAxisID: "y2",
                    stack: "frequency",
                },
                {
                    type: "bar",
                    label: "Losses",
                    data: losses,
                    backgroundColor: "rgba(244, 67, 54, 0.6)",
                    borderColor: "#F44336",
                    borderWidth: 1,
                    yAxisID: "y2",
                    stack: "frequency",
                },
            ],
        };
    };

    const chartData = buildChartData();
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true, position: "top" },
            title: { display: false },
        },
        scales: {
            y: {
                type: "linear",
                position: "left",
                title: { display: true, text: "P/L ($)" },
                ticks: { color: "#fff" },
                grid: { color: "#444" },
            },
            y1: {
                type: "linear",
                position: "right",
                title: { display: true, text: "Win Rate (%)" },
                ticks: { color: "#fff" },
                grid: { drawOnChartArea: false },
            },
            y2: {
                type: "linear",
                position: "right",
                title: { display: true, text: "Frequency" },
                ticks: { color: "#fff" },
                grid: { drawOnChartArea: false },
                offset: true,
            },
            x: {
                ticks: { color: "#fff" },
                grid: { color: "#444" },
            },
        },
    };

    const renderPatternMessages = () => {
        if (!nearOpenPattern) {
            return <span>No data available.</span>;
        }

        let label = nearOpenPattern.pattern_label;
        let description = nearOpenPattern.pattern_meaning;

        const threshold1 = nearOpenPattern.threshold_1_used;
        const threshold2 = nearOpenPattern.threshold_2_used;
        const lookbackWindow = nearOpenPattern.default_lookback_window_used;

        const symbolMap = {};
        if (Array.isArray(nearOpenPattern.details)) {
            nearOpenPattern.details.forEach((item) => {
                if (!symbolMap[item.Symbol]) {
                    symbolMap[item.Symbol] = 1;
                } else {
                    symbolMap[item.Symbol] += 1;
                }
            });
        }

        const groupedDetails = Object.entries(symbolMap)
            .slice(0, 10)
            .map(([symbol, count]) => ({ symbol, count }));

        const moreDetails = (
            <div style={{ margin: "8px 0" }}>
                <a
                    href="#"
                    style={{
                        color: "#4fa3f7",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: "0.97em",
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                        setShowDetails((prev) => !prev);
                    }}
                >
                    {showDetails ? "Hide details" : "More details"}
                </a>
                {showDetails && (
                    <div
                        style={{
                            marginTop: 6,
                            color: "#aaa",
                            fontSize: "0.97em",
                        }}
                    >
                        <div>
                            <b>Market open:</b> {formatTime(threshold1)}
                            <br />
                            <b>Time after open:</b> {formatTime(threshold2)}
                        </div>
                        <div>
                            <b>Lookback window:</b> {lookbackWindow || nearOpenPattern.lookback_days || 30} days
                        </div>
                        <div>
                            <b>Pattern matches:</b> {nearOpenPattern.pattern_matches || 0}
                        </div>
                        {nearOpenPattern.recent_events_30d !== undefined && (
                            <div>
                                <b>Recent events (30 days):</b> {nearOpenPattern.recent_events_30d}
                            </div>
                        )}
                        {/* ✅ NEW: Show cache status */}
                        {fromCache && (
                            <div style={{ marginTop: 6, fontSize: "0.9em", color: "#888" }}>
                                📦 Data loaded from database cache (4 hour TTL)
                            </div>
                        )}
                    </div>
                )}
            </div>
        );

        return (
            <div style={{ marginBottom: 8 }}>
                <span>
                    {label || "Emerging Pattern"}: {description}
                </span>
                {moreDetails}
                {groupedDetails.length > 0 && (
                    <div style={{ marginTop: 8, color: "#ccc", fontSize: "0.97em" }}>
                        {groupedDetails.map((item, idx) => (
                            <div key={idx}>
                                {item.symbol}: {item.count} first half hour open(s)
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderChartToggles = () => (
        <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
            {["cumulative", "rolling", "scatter"].map((mode) => {
                const buttonLabels = {
                    cumulative: "Cumulative",
                    rolling: "Rolling Avg",
                    scatter: "Trade Scatter"
                };
                const buttonLabel = buttonLabels[mode];
                const subcomponentLabel = mode === "cumulative" 
                    ? "Cumulative Chart" 
                    : mode === "rolling" 
                    ? "Rolling Avg Chart" 
                    : "Trade Scatter Chart";

                return (
                    <div key={mode} data-analytics={MODULE_NAME}>
                        <button
                            className={`btn ${chartMode === mode ? "btn-selected" : ""}`}
                            onClick={() => {
                                logEvent({
                                    event_type: "button_click",
                                    user_id: userId,
                                    module_name: MODULE_NAME,
                                    subcomponent: subcomponentLabel,
                                    screen: window.location.pathname,
                                    metadata: {
                                        button_link_value: buttonLabel
                                    }
                                });
                                setChartMode(mode);
                            }}
                            style={{
                                padding: "6px 14px",
                                borderRadius: 6,
                                border: "1px solid #888",
                                background: chartMode === mode ? "#1976d2" : "#f5f5f5",
                                color: chartMode === mode ? "#fff" : "#222",
                                fontWeight: 500,
                                cursor: "pointer",
                                transition: "background 0.2s",
                                fontSize: "0.98em",
                            }}
                        >
                            {buttonLabel}
                        </button>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div
            ref={chartContainerRef}
            style={{
                width: "100%",
                background: "none",
                borderRadius: 0,
                padding: 0,
                margin: 0,
                maxWidth: "100%",
                position: "relative"
            }}
        >
            <h3
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: "1.25em",
                    margin: "0 0 8px 0",
                    color: "#fff",
                }}
            >
                {nearOpenPattern?.pattern_name || "First Half Hour Trading"}{" "}
                {tooltip && (
                    <span
                        className="tooltip-icon"
                        style={{
                            marginLeft: 6,
                            verticalAlign: "middle",
                            cursor: "pointer",
                            fontSize: "1.1em",
                        }}
                        title={tooltip}
                    >
                        🛈
                    </span>
                )}
                <span
                    style={{ marginLeft: 8 }}
                    title="Edit pattern settings"
                    onClick={() => {
                        logEvent({
                            event_type: "button_click",
                            user_id: userId,
                            module_name: MODULE_NAME,
                            subcomponent: "Settings Gear",
                            screen: window.location.pathname,
                            metadata: { button_link_value: "Settings Gear" }
                        });
                        setShowSettings(true);
                    }}
                >
                    <GearIcon size={24} color="#fff" />
                </span>
            </h3>
            {renderSettingsModal()}
            <div style={{ minHeight: 32, fontSize: "1.05em", marginBottom: 16 }}>
                {loading ? (
                    <span>Loading...</span>
                ) : (
                    <>
                        <span >
                        </span>
                        {typeof totalPL === "number" && (
                            <div
                                style={{
                                    marginTop: 6,
                                    color: "#e0b400",
                                    fontSize: "1.1em",
                                }}
                            >
                                <b>P/L:</b>{" "}
                                <span
                                    style={{
                                        color: totalPL >= 0 ? "#4caf50" : "#e53935",
                                    }}
                                >
                                    ${totalPL.toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </span>
                            </div>
                        )}
                        <div style={{ marginTop: 8 }}>{renderPatternMessages()}</div>
                        <div style={{ marginTop: 10 }}>
                            {renderChartToggles()}
                            <div style={{ position: "relative", width: "100%", minHeight: 320, maxWidth: "100%" }}>
                                {chartData.datasets.length > 0 ? (
                                    <Chart
                                        type="bar"
                                        data={chartData}
                                        options={{
                                            ...chartOptions,
                                            responsive: true,
                                            maintainAspectRatio: false,
                                        }}
                                    />
                                ) : (
                                    <div style={{ color: "#888", padding: "20px", textAlign: "center" }}>
                                        No chart data available for this period.
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Pattern_near_open;